import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, User, Clock, Edit, History, Building2, Filter, X } from 'lucide-react';
import AuthContext from '../../../../../context/AuthContext';
import superAdminAuditService from '../../../../../services/audit_services/superAdminAuditService';

export default function AllAuditProgramsHistory() {
  const navigate = useNavigate();
  const { authTokens } = useContext(AuthContext);
  
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState('');
  
  // Filters
  const [filters, setFilters] = useState({
    search: '',
    action_type: '',
    ordering: 'newest'
  });
  
  // Pagination
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 20,
    total_count: 0,
    total_pages: 0
  });

  useEffect(() => {
    fetchHistory();
  }, [pagination.page, pagination.page_size]);

  const fetchHistory = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        ...filters,
        page: pagination.page,
        page_size: pagination.page_size
      };
      
      const result = await superAdminAuditService.getAuditProgramHistory(authTokens.access, params);
      
      setHistory(result.results);
      setPagination(prev => ({
        ...prev,
        total_count: result.count,
        total_pages: result.total_pages
      }));
    } catch (err) {
      setError(err.message || 'Erreur lors du chargement de l\'historique');
      setMessage(err.message || 'Erreur lors du chargement de l\'historique');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const applyFilters = () => {
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchHistory();
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      action_type: '',
      ordering: 'newest'
    });
    setPagination(prev => ({ ...prev, page: 1 }));
    setTimeout(() => fetchHistory(), 100);
  };

  const renderValue = (value) => {
    if (value === null || value === undefined) return 'null';
    if (typeof value === 'object') {
      if (Array.isArray(value)) {
        return `[${value.map(item => renderValue(item)).join(', ')}]`;
      }
      if (value.email) return value.email;
      if (value.name) return value.name;
      if (value.title) return value.title;
      return JSON.stringify(value);
    }
    return String(value);
  };

  const renderChanges = (changes) => {
    if (!changes || Object.keys(changes).length === 0) {
      return <p className="text-sm text-gray-500">Aucun changement détaillé enregistré.</p>;
    }

    return (
      <ul className="space-y-2">
        {Object.entries(changes).map(([field, values]) => (
          <li key={field} className="text-sm">
            <strong className="font-semibold text-gray-800">{field}:</strong>
            <span className="ml-2">
              <span className="text-red-600">'{renderValue(values.from)}'</span> 
              <span className="mx-2">→</span> 
              <span className="text-green-600">'{renderValue(values.to)}'</span>
            </span>
          </li>
        ))}
      </ul>
    );
  };

  const getActionLabel = (actionType) => {
    const labels = {
      'create': 'Création',
      'update': 'Modification',
      'delete': 'Suppression',
      'close': 'Fermeture',
      'reopen': 'Réouverture',
      'achieve': 'Archivage',
      'unachieve': 'Désarchivage',
      'add_party': 'Ajout de partie',
      'remove_party': 'Retrait de partie'
    };
    return labels[actionType] || actionType;
  };

  const getActionColor = (actionType) => {
    const colors = {
      'create': 'bg-green-100 text-green-800',
      'update': 'bg-blue-100 text-blue-800',
      'delete': 'bg-red-100 text-red-800',
      'close': 'bg-gray-100 text-gray-800',
      'reopen': 'bg-purple-100 text-purple-800',
      'achieve': 'bg-indigo-100 text-indigo-800',
      'unachieve': 'bg-yellow-100 text-yellow-800',
      'add_party': 'bg-teal-100 text-teal-800',
      'remove_party': 'bg-orange-100 text-orange-800'
    };
    return colors[actionType] || 'bg-gray-100 text-gray-800';
  };

  if (loading && history.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <button
          onClick={() => navigate('/admin-panel/audit-programs')}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-6"
        >
          <ArrowLeft className="h-5 w-5 mr-2" />
          Retour aux programmes d'audit
        </button>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-bold text-gray-900">Historique de tous les Programmes d'Audit</h1>
            <div className="flex items-center text-gray-500">
              <History className="h-5 w-5 mr-2" />
              <span>{pagination.total_count} entrée(s) d'historique</span>
            </div>
          </div>

          {message && (
            <div className={`${error ? 'bg-red-50 border-red-200 text-red-800' : 'bg-green-50 border-green-200 text-green-800'} border rounded-lg p-4 mb-6`}>
              <p>{message}</p>
            </div>
          )}

          {/* Filters */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-gray-700 flex items-center">
                <Filter className="h-4 w-4 mr-2" />
                Filtres
              </h3>
              <button
                onClick={clearFilters}
                className="text-sm text-gray-600 hover:text-gray-900 flex items-center"
              >
                <X className="h-4 w-4 mr-1" />
                Effacer les filtres
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Recherche</label>
                <input
                  type="text"
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  placeholder="Titre ou description..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type d'action</label>
                <select
                  value={filters.action_type}
                  onChange={(e) => handleFilterChange('action_type', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="">Tous</option>
                  <option value="create">Création</option>
                  <option value="update">Modification</option>
                  <option value="delete">Suppression</option>
                  <option value="close">Fermeture</option>
                  <option value="reopen">Réouverture</option>
                  <option value="achieve">Archivage</option>
                  <option value="unachieve">Désarchivage</option>
                  <option value="add_party">Ajout de partie</option>
                  <option value="remove_party">Retrait de partie</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tri</label>
                <select
                  value={filters.ordering}
                  onChange={(e) => handleFilterChange('ordering', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="newest">Plus récent</option>
                  <option value="oldest">Plus ancien</option>
                </select>
              </div>
            </div>
            
            <div className="mt-4">
              <button
                onClick={applyFilters}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
              >
                Appliquer les filtres
              </button>
            </div>
          </div>

          {history.length === 0 && !loading && (
            <div className="text-center py-12">
              <History className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <p className="text-lg font-medium text-gray-900">Aucun historique trouvé</p>
              <p className="text-gray-500">Aucun enregistrement d'historique ne correspond à vos critères.</p>
            </div>
          )}

          <div className="space-y-6">
            {history.map((entry, index) => (
              <div key={entry.id} className="border border-gray-200 rounded-lg p-6 relative hover:shadow-md transition-shadow">
                {index < history.length - 1 && (
                  <div className="absolute left-6 top-16 w-0.5 h-6 bg-gray-300"></div>
                )}
                
                <div className="flex items-start space-x-4">
                  <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                    entry.action_type === 'create' ? 'bg-green-100 text-green-600' :
                    entry.action_type === 'update' ? 'bg-blue-100 text-blue-600' :
                    entry.action_type === 'delete' ? 'bg-red-100 text-red-600' :
                    'bg-gray-100 text-gray-600'
                  }`}>
                    <Edit className="h-4 w-4" />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getActionColor(entry.action_type)}`}>
                        {getActionLabel(entry.action_type)}
                      </span>
                      
                      <div className="flex items-center text-sm text-gray-500 space-x-4 flex-wrap gap-2">
                        {entry.organisation_name && (
                          <div className="flex items-center">
                            <Building2 className="h-4 w-4 mr-1" />
                            <span>{entry.organisation_name}</span>
                          </div>
                        )}
                        <div className="flex items-center">
                          <User className="h-4 w-4 mr-1" />
                          <span>{entry.done_by_name || entry.done_by_email || 'N/A'}</span>
                        </div>
                        <div className="flex items-center">
                          <Calendar className="h-4 w-4 mr-1" />
                          <span>{new Date(entry.action_date).toLocaleDateString('fr-FR')}</span>
                        </div>
                        <div className="flex items-center">
                          <Clock className="h-4 w-4 mr-1" />
                          <span>{new Date(entry.action_date).toLocaleTimeString('fr-FR')}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="mb-3">
                      <p className="text-gray-700 font-medium">{entry.description}</p>
                      {entry.audit_program_title && (
                        <p className="text-sm text-gray-600 mt-1">Programme: {entry.audit_program_title}</p>
                      )}
                    </div>
                    
                    {entry.changes_summary && Object.keys(entry.changes_summary).length > 0 && (
                      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <h4 className="text-sm font-semibold text-gray-800 mb-2">Changements détaillés:</h4>
                        {renderChanges(entry.changes_summary)}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {pagination.total_pages > 1 && (
            <div className="mt-6 flex items-center justify-between border-t border-gray-200 pt-4">
              <div className="flex items-center space-x-2">
                <label className="text-sm text-gray-700">Éléments par page:</label>
                <select
                  value={pagination.page_size}
                  onChange={(e) => setPagination(prev => ({ ...prev, page_size: parseInt(e.target.value), page: 1 }))}
                  className="px-3 py-1 border border-gray-300 rounded-md"
                >
                  <option value="10">10</option>
                  <option value="20">20</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
              </div>
              
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                  disabled={pagination.page === 1}
                  className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Précédent
                </button>
                <span className="text-sm text-gray-700">
                  Page {pagination.page} sur {pagination.total_pages}
                </span>
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: Math.min(prev.total_pages, prev.page + 1) }))}
                  disabled={pagination.page === pagination.total_pages}
                  className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Suivant
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
